import type React from 'react';
import { BrainCircuit, Bug, Clock, Edit3, Image as ImageIcon, MapPin, MessageSquare, Newspaper, PenTool, Route, ScanLine, ShieldAlert, ShieldCheck, TrendingUp, UserCheck, Youtube } from 'lucide-react';

export type AdminTab = 'spots' | 'suggestions' | 'routes' | 'pending' | 'tools' | 'ai' | 'testers' | 'privacy' | 'messages' | 'campaigns' | 'articles' | 'creators' | 'prescan' | 'imagescan' | 'googlescan' | 'errors';

export type AdminTabMeta = {
  label: string;
  subtitle: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  iconTone: string;
};

export const getAdminTabMeta = (t: (key: string) => string): Record<AdminTab, AdminTabMeta> => ({
  spots: {
    label: t('admin_nav.spots'),
    subtitle: t('admin_nav.spots_sub'),
    icon: ShieldAlert,
    iconTone: 'bg-red-500/10 text-red-500 border-red-500/20'
  },
  suggestions: {
    label: t('admin_nav.suggestions'),
    subtitle: t('admin_nav.suggestions_sub'),
    icon: Edit3,
    iconTone: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
  },
  pending: {
    label: t('admin_nav.pending'),
    subtitle: t('admin_nav.pending_sub'),
    icon: Clock,
    iconTone: 'bg-amber-500/10 text-amber-500 border-amber-500/20'
  },
  routes: {
    label: t('admin_nav.routes'),
    subtitle: t('admin_nav.routes_sub'),
    icon: Route,
    iconTone: 'bg-blue-500/10 text-blue-500 border-blue-500/20'
  },
  tools: {
    label: t('admin_nav.tools'),
    subtitle: t('admin_nav.tools_sub'),
    icon: PenTool,
    iconTone: 'bg-purple-500/10 text-purple-500 border-purple-500/20'
  },
  ai: {
    label: t('admin_nav.ai'),
    subtitle: t('admin_nav.ai_sub'),
    icon: BrainCircuit,
    iconTone: 'bg-orange-500/10 text-orange-500 border-orange-500/20'
  },
  testers: {
    label: t('admin_nav.testers'),
    subtitle: t('admin_nav.testers_sub'),
    icon: UserCheck,
    iconTone: 'bg-pink-500/10 text-pink-500 border-pink-500/20'
  },
  privacy: {
    label: t('admin_nav.privacy'),
    subtitle: t('admin_nav.privacy_sub'),
    icon: ShieldCheck,
    iconTone: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
  },
  messages: {
    label: t('admin_nav.messages'),
    subtitle: t('admin_nav.messages_sub'),
    icon: MessageSquare,
    iconTone: 'bg-sky-500/10 text-sky-400 border-sky-500/20'
  },
  campaigns: {
    label: t('admin_nav.campaigns'),
    subtitle: t('admin_nav.campaigns_sub'),
    icon: TrendingUp,
    iconTone: 'bg-violet-500/10 text-violet-400 border-violet-500/20'
  },
  articles: {
    label: t('admin_nav.articles'),
    subtitle: t('admin_nav.articles_sub'),
    icon: Newspaper,
    iconTone: 'bg-teal-500/10 text-teal-400 border-teal-500/20'
  },
  creators: {
    label: t('admin_nav.creators'),
    subtitle: t('admin_nav.creators_sub'),
    icon: Youtube,
    iconTone: 'bg-red-500/10 text-red-400 border-red-500/20'
  },
  prescan: {
    label: t('admin_nav.prescan'),
    subtitle: t('admin_nav.prescan_sub'),
    icon: ScanLine,
    iconTone: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'
  },
  imagescan: {
    label: t('admin_nav.imagescan'),
    subtitle: t('admin_nav.imagescan_sub'),
    icon: ImageIcon,
    iconTone: 'bg-violet-500/10 text-violet-400 border-violet-500/20'
  },
  googlescan: {
    label: 'Google steder',
    subtitle: 'Finn steder OSM mangler',
    icon: MapPin,
    iconTone: 'bg-green-500/10 text-green-400 border-green-500/20'
  },
  errors: {
    label: t('admin_nav.errors'),
    subtitle: t('admin_nav.errors_sub'),
    icon: Bug,
    iconTone: 'bg-red-500/10 text-red-400 border-red-500/20'
  },
});

export const getAdminDesktopSections = (t: (key: string) => string): Array<{ title: string; tabs: AdminTab[] }> => [
  { title: t('admin_nav.section_inbox'), tabs: ['testers', 'messages'] },
  // Kommunikasjonssenter — utgående kommunikasjon til brukere (artikler/versjonsnotater + kampanje-epost).
  { title: t('admin_nav.section_communication', 'Kommunikasjon'), tabs: ['articles', 'campaigns'] },
  { title: t('admin_nav.section_content'), tabs: ['spots', 'suggestions', 'creators'] },
  { title: t('admin_nav.section_routes'), tabs: ['pending', 'routes'] },
  { title: t('admin_nav.section_system'), tabs: ['tools', 'ai', 'privacy', 'prescan', 'imagescan', 'googlescan', 'errors'] }
];
